import React, { ChangeEvent, createRef, MouseEvent, useEffect, useState } from 'react';
import TextContent from '../textContent/textContent';
import { withTranslation, WithTranslation } from 'react-i18next';

import ContactStep1 from './contact/step1/contactStep1';
import OrganizationStep1 from './organization/step1/organizationStep1';

import Notification, { INotificationErrorMessage } from '../notification/notification';
import OrganizationStep2 from './organization/step2/organizationStep2';
import OrganizationStep3 from './organization/step3/organizationStep3';
import OrganizationBreadcrumb from './organization/organizationBreadcrumb';
import PopoverContainer from '../popover/popover';
import ProfessionalBreadcrumb from './professional/professionalBreadcrumb';
import ProfessionalStep1 from './professional/step1/professionalStep1';
import ProfessionalStep2 from './professional/step2/professionalStep2';
import OrganizationReviseSubmit from './organization/reviseSubmit/organizationReviseSubmit';
import ProfessionalReviseSubmit from './professional/reviseSubmit/professionalReviseSubmit';
import { EAccountType, IAccountModel } from '../../helpers/models/accountModel';
import { fetchAccount, resetAccount, updateAccount } from '../../helpers/account';
import { IProvinceModel } from '../../helpers/models/provinceModel';
import { IRoleModel } from '../../helpers/models/roleModel';
import { isErrorModel } from '../../helpers/models/errorModel';
import { IFormModalRef } from '../modal/modal';
import FormEligibilityModal, { IFormEligibilityModalRef } from '../modal/instances/eligibility/formEligibilityModal';
import getProvinces from '../../helpers/provinces';
import getRoles from '../../helpers/role';
import FormResetConfirmationModal from '../modal/instances/formResetConfirmationModal';
import config from '../../config';

interface IRegistrationProps extends WithTranslation {
    accountId: string | null;
    registrationId: string | null;
}

export const termsAndConditionsEn = config.STATIC_ASSETS_URL + '/pdf/en/PROVIDER_AGREEMENT_En.pdf';

export const termsAndConditionsFr = config.STATIC_ASSETS_URL + '/pdf/fr/PROVIDER_AGREEMENT_Fr.pdf';
export const startOverId = 'start_over_button';

/**
 * The target element (input) must have an id, since its label is linked to it.
 * If no id is provided, an error will be thrown.
 * */
export const handleInputWithLabelOnBlur = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.parentElement!.classList.remove('field-focused');
};

/**
 * The target element (input) must have an id, since its label is linked to it.
 * If no id is provided, an error will be thrown.
 * */
export const handleInputWithLabelOnFocus = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.parentElement!.classList.add('field-focused');
};

export const getAccessibilityTextFromStep = (index: number, currentStep: number) => {
    return currentStep === index
        ? 'breadcrumb_in_progress'
        : currentStep < index
        ? 'breadcrumb_to_do'
        : 'breadcrumb_completed';
};

export const accountFromFormResult = (formResult: any, formData: IAccountModel) => {
    const accountDetails = Object.assign({}, formData);

    accountDetails.firstname = formResult.registration_contact_first_name;
    accountDetails.lastname = formResult.registration_contact_last_name;
    accountDetails.phone = formResult.registration_contact_phone;
    accountDetails.ext = formResult.registration_contact_phone_ext;
    accountDetails.email = formResult.registration_contact_email;
    accountDetails.province = +(formData.province as IProvinceModel).id;
    accountDetails.role = +(formData.role as IRoleModel).id;
    if (accountDetails.step >= 1) {
        accountDetails.step++;
    }
    return accountDetails;
};

const Registration = (props: IRegistrationProps) => {
    const { t, i18n } = props;
    const [error, setError] = useState<INotificationErrorMessage[]>();
    const [hasPreFill, setPreFill] = useState<boolean>(
        (props.accountId && props.registrationId) !== null
    );
    const [formData, setFormData] = useState<IAccountModel | undefined>();
    const eligibilityRef = createRef<IFormModalRef & IFormEligibilityModalRef>();
    const resetConfirmationRef = createRef<IFormModalRef>();

    const [provinces, setProvinces] = useState<IProvinceModel[]>([]);
    const [roles, setRoles] = useState<IRoleModel[]>([]);

    async function getData(lang: string) {
        const provincesList = await getProvinces(lang);
        setProvinces(provincesList ?? []);

        const roleList = await getRoles(lang);
        setRoles(roleList ?? []);
    }

    async function getAccountData(lang: string, accountId: string) {
        const accountDetails = await fetchAccount(lang, Number(accountId));
        accountDetails && setFormData(accountDetails);

        if (hasPreFill && accountDetails!.step === 0) {
            setPreFill(false);
        }
    }

    useEffect(() => {
        getData(i18n.language);
        if (hasPreFill) {
            getAccountData(i18n.language, props.accountId!);
        }
    }, [i18n.language]);

    const headerSubtitleComponent = (
        <>
            <FormEligibilityModal provinces={provinces} roles={roles} ref={eligibilityRef} />
            <a
                className="standalone-link standalone-link--large"
                onClick={() => {
                    eligibilityRef.current?.setModalOpen(true);
                }}
                tabIndex={0}
                role="button"
            >
                {t('form_contact_subtitle_cta_title')}
            </a>
        </>
    );

    const headerDescriptionComponent = (
        <PopoverContainer
            triggerContent={t('form_contact_description_cta_title')}
            prefix={t('form_contact_description_cta_title')}
        >
            <div className="body-medium">
                <div>{t('form_contact_popover_tel')}</div>
                <a
                    tabIndex={-1}
                    className="standalone-link"
                    href={`mailto:${t('form_contact_popover_email')}`}
                >
                    {t('form_contact_popover_email')}
                </a>
            </div>
        </PopoverContainer>
    );

    const errorComponent = (
        <div id="registration-notification-container" className="registration-error-container">
            <Notification error messages={error ?? []} />
        </div>
    );

    const handleGoBack = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (formData) {
            const newFormData = Object.assign({}, formData);
            if (newFormData.step > 1) {
                newFormData.step--;
            }
            setFormData(newFormData);

            // set number to province and role instead of the returned object
            const newFormDataUpdated = Object.assign({}, newFormData);
            newFormDataUpdated.province = Number((formData?.province as IProvinceModel).id);
            newFormDataUpdated.role = Number((formData?.role as IRoleModel).id);
            updateAccount(i18n.language, newFormData.id, { ...newFormDataUpdated });

            window.scrollTo(0, 0);
        }
    };

    const handleStartOver = async (e: MouseEvent<HTMLAnchorElement>, type: EAccountType) => {
        e.preventDefault();
        if (
            document.activeElement?.id === startOverId ||
            document.activeElement === document.body
        ) {
            resetConfirmationRef.current?.setModalOpen(true);
        }
    };

    const handleStartOverConfirmation = async () => {
        const response = await resetAccount(i18n.language, props.accountId!);
        if (!isErrorModel(response)) {
            window.location.reload();
        }
    };

    let formStep = (
        <ContactStep1 setError={setError} account={formData} eligibilityRef={eligibilityRef} />
    );

    if (hasPreFill) {
        // return organization flow if the specialist grp chosen is optician
        if (formData?.type === EAccountType.ORGANIZATION) {
            if (formData?.step === 1) {
                formStep = (
                    <OrganizationStep1
                        setError={setError}
                        account={formData}
                        startOver={(e: any) => handleStartOver(e, EAccountType.ORGANIZATION)}
                    />
                );
            } else if (formData?.step === 2) {
                formStep = (
                    <OrganizationStep2
                        setError={setError}
                        account={formData}
                        goBack={handleGoBack}
                        startOver={(e: any) => handleStartOver(e, EAccountType.ORGANIZATION)}
                    />
                );
            } else if (formData?.step === 3) {
                formStep = (
                    <OrganizationStep3
                        setError={setError}
                        account={formData}
                        goBack={handleGoBack}
                        startOver={(e: any) => handleStartOver(e, EAccountType.ORGANIZATION)}
                    />
                );
            } else if (formData?.step === 4) {
                formStep = (
                    <OrganizationReviseSubmit
                        setError={setError}
                        account={formData}
                        goBack={handleGoBack}
                        startOver={(e: any) => handleStartOver(e, EAccountType.ORGANIZATION)}
                    />
                );
            }
        } else if (formData?.type === EAccountType.PROFESSIONAL) {
            if (formData?.step === 1) {
                formStep = (
                    <ProfessionalStep1
                        setError={setError}
                        account={formData}
                        startOver={(e: any) => handleStartOver(e, EAccountType.PROFESSIONAL)}
                    />
                );
            } else if (formData?.step === 2) {
                formStep = (
                    <ProfessionalStep2
                        setError={setError}
                        account={formData}
                        goBack={handleGoBack}
                        startOver={(e: any) => handleStartOver(e, EAccountType.PROFESSIONAL)}
                    />
                );
            } else if (formData?.step === 3) {
                formStep = (
                    <ProfessionalReviseSubmit
                        setError={setError}
                        account={formData}
                        goBack={handleGoBack}
                        startOver={(e: any) => handleStartOver(e, EAccountType.PROFESSIONAL)}
                    />
                );
            }
        }
    }

    return (
        <main>
            {hasPreFill ? (
                (formData?.type === EAccountType.PROFESSIONAL && formData.step !== 3) ||
                (formData?.type === EAccountType.ORGANIZATION && formData.step !== 4) ? (
                    <div className="home-component">
                        {formData?.type === EAccountType.ORGANIZATION ? (
                            <OrganizationBreadcrumb currentStep={formData!.step} />
                        ) : formData?.type === EAccountType.PROFESSIONAL ? (
                            <ProfessionalBreadcrumb currentStep={formData!.step} />
                        ) : null}
                    </div>
                ) : null
            ) : null}

            {hasPreFill ? (
                (formData?.type === EAccountType.PROFESSIONAL && formData.step === 3) ||
                (formData?.type === EAccountType.ORGANIZATION && formData.step === 4) ? (
                    <>
                        <div className="home-component">
                            <div className="registration-revise-submit-header-container">
                                <h2 className="header-3 header-3--purple">
                                    {t('form_revise_submit_title')}
                                </h2>
                            </div>
                        </div>
                    </>
                ) : null
            ) : null}

            <div id="registration-form" className="home-component home-component--registration">
                <FormResetConfirmationModal
                    ref={resetConfirmationRef}
                    onCloseAndContinue={handleStartOverConfirmation}
                />
                {!hasPreFill ? (
                    <TextContent
                        descriptionCTAComponent={headerDescriptionComponent}
                        subtitleCTAComponent={headerSubtitleComponent}
                        title="form_contact_title"
                        description="form_contact_description"
                    />
                ) : null}
                {error?.length ? errorComponent : null}
                {(formData?.type === EAccountType.PROFESSIONAL && formData.step === 3) ||
                (formData?.type === EAccountType.ORGANIZATION && formData.step === 4)
                    ? null
                    : formStep}
            </div>
            {((formData?.type === EAccountType.PROFESSIONAL && formData.step === 3) ||
                (formData?.type === EAccountType.ORGANIZATION && formData.step === 4)) &&
                formStep}
        </main>
    );
};

export default withTranslation()(Registration);
